import type { InitialConfigType, LexicalEditor } from '@fluentui-copilot/react-text-editor';
import { $createParagraphNode } from '@fluentui-copilot/react-text-editor';
import { $getRoot } from '@fluentui-copilot/react-text-editor';
import { createEditor } from '@fluentui-copilot/react-text-editor';
import { useIsomorphicLayoutEffect } from '@fluentui/react-utilities';
import * as React from 'react';

// Merge initial state with history
const UPDATE_OPTIONS = { tag: 'history-merge' };

function setInitialState(editor: LexicalEditor, initialState: InitialConfigType['editorState']) {
  if (initialState === null) {
    return;
  } else if (initialState === undefined) {
    editor.update(() => {
      const root = $getRoot();
      if (root.isEmpty()) {
        root.append($createParagraphNode());
      }
    }, UPDATE_OPTIONS);
  } else {
    switch (typeof initialState) {
      case 'string': {
        const parsedState = editor.parseEditorState(initialState);
        editor.setEditorState(parsedState, UPDATE_OPTIONS);
        break;
      }
      case 'object': {
        editor.setEditorState(initialState, UPDATE_OPTIONS);
        break;
      }
      case 'function': {
        editor.update(() => {
          if ($getRoot().isEmpty()) {
            initialState(editor);
          }
        }, UPDATE_OPTIONS);
      }
    }
  }
}

export function useLexicalEditor(initialConfig: InitialConfigType): LexicalEditor {
  const { theme, namespace, nodes, onError, editorState: initialEditorState, html, editable = true } = initialConfig;

  const [lexicalEditor] = React.useState(() => {
    const lexicalEditor = createEditor({
      editable,
      html,
      namespace,
      nodes,
      onError: error => onError(error, lexicalEditor),
      theme,
    });

    setInitialState(lexicalEditor, initialEditorState);

    return lexicalEditor;
  });

  useIsomorphicLayoutEffect(() => {
    const isEditable = initialConfig.editable;
    lexicalEditor.setEditable(isEditable !== undefined ? isEditable : true);
  }, []);

  return lexicalEditor;
}
